import React, { useRef } from 'react';
import { graphql, Link } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import { get } from 'lodash';
import {
  faChevronRight,
  faBellConcierge,
  faChain,
  faInfo,
  faEnvelope,
  faRocket,
  faBlog,
  faGearCode,
  faArrowRightLong,
} from '@fortawesome/pro-light-svg-icons';
import { faVideoCamera } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@/components/Button';
import { StayInTouch } from '@/components/StayInTouch';
import { Social } from '@/components/Social';
import { useActiveOnIntersect } from '@/hooks/useActiveOnIntersect';
import { EmployeeCard } from '@/components/EmployeeCard';
import { SectionHeader } from '@/components/SectionHeader';
import { TechTable } from '@/components/TechTable';
import { ProjectsTable } from '@/components/ProjectsTable';
import { Rectangle } from '@/components/Rectangle';
import { ProcessesSlider } from '@/components/ProcessesSlider';
import { ServicesSlider } from '@/components/ServicesSlider';
import { SlidingText } from '@/components/SlidingText';
import { ArticlesTable } from '@/components/ArticlesTable';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { Layout, Main } from '../../components/Layout';
import { Content } from '../../components/Content';
import { Text } from '../../components/Text';
import { MdContent } from '../../components/MdContent';
import { HeaderWithMore } from '../../components/HeaderWithMore';
import { Seo } from '../../components/SEO';
import { CallToAction } from '../../components/CallToAction';
import { StyledHome } from './styles';
import { useStateValue } from '../../state';
import { Banner } from '@/components/Banner';
import { ContactList } from '@/components/ContactList';

interface IHomePageTemplate {
  content: any;
}

interface IHomePage {
  data: {
    site: {
      siteMetadata: {
        title: string;
        siteUrl: string;
      };
    };
    contentfulIndex: any;
  };
  location: object;
}

const HomePageTemplate = ({ content }: IHomePageTemplate) => {
  const {
    title,
    titleSlides,
    welcomeNote,
    servicesTitle,
    servicesSubtitle,
    services,
    bannerTitle,
    bannerBody,
    bannerCta,
    projectsTitle,
    projects,
    technologiesTitle,
    technologies,
    articlesTitle,
    articles,
    teamTitle,
    teamSubtitle,
    teamMembers,
    ctaContactTitle,
    ctaTitle,
    ctaHookId,
    ctaIcon,
    ctaBody,
    pages,
    processes,
    contactTitle,
    contactSubtitle,
    contactBodyBefore,
    contactBodyAfter,
  } = content;
  const { body: aboutBody } = pages.find(({ slug }) => slug === `about`);
  const { name: contactName } = pages.find(({ slug }) => slug === `contact`);
  const { name: processesName } = pages.find(
    ({ slug }) => slug === `processes`,
  );
  return (
    <>
      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-welcome"
      >
        <Text type="title1" tag="h2" color="white_1" className="main-title">
          {title}
        </Text>
        <SlidingText list={titleSlides} />
        <Text type="body1" tag="div" color="gray_3">
          <MdContent content={welcomeNote} />
        </Text>
        <Link
          to="/presentation"
          state={{
            modal: true,
          }}
        >
          <Button size="large" disabled={false} iconLeft={faVideoCamera}>
            Play intro
          </Button>
        </Link>
      </Content>

      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-services"
      >
        <HeaderWithMore linkTo="services" linkText="Learn more">
          <Text type="title3" tag="h3" color="gray_3">
            {servicesTitle}
          </Text>
          <Text
            type="subtitle2"
            tag="h5"
            color="white_1_45"
            className="section-subtitle"
          >
            {servicesSubtitle}
          </Text>
        </HeaderWithMore>
        <ServicesSlider services={services} />
      </Content>

      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-banner"
      >
        <Banner title={bannerTitle} content={bannerBody} cta={bannerCta} />
      </Content>

      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-processes"
      >
        <HeaderWithMore linkTo="processes" linkText="Learn more">
          <Text type="title3" tag="h3" color="gray_3">
            {processesName}
          </Text>
        </HeaderWithMore>
        <ProcessesSlider processes={processes} />
      </Content>

      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-projects"
      >
        <HeaderWithMore linkTo="projects" linkText="All projects">
          <Text type="title3" tag="h3" color="gray_3">
            {projectsTitle}
          </Text>
        </HeaderWithMore>
        <ProjectsTable data={projects} />
      </Content>

      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-technologies"
      >
        <HeaderWithMore>
          <Text type="title3" tag="h3" color="gray_3">
            {technologiesTitle}
          </Text>
        </HeaderWithMore>
        <TechTable data={technologies} />
      </Content>

      <Content restrictWidth setMaxWidth="75%" className="mobile-content-blog">
        <HeaderWithMore
          linkTo="blog"
          linkText="All blog posts"
          className="mobile-content-blog__title"
        >
          <Text type="title3" tag="h3" color="gray_3">
            {articlesTitle}
          </Text>
        </HeaderWithMore>
        <ArticlesTable articles={articles} />
      </Content>

      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-members last"
      >
        <HeaderWithMore
          className="mobile-content-members__title"
          linkTo="about"
          linkText="About us"
        >
          <Text type="title3" tag="h3" color="gray_3">
            {teamTitle}
          </Text>
          <Text
            type="subtitle2"
            tag="h5"
            color="white_1_45"
            className="section-subtitle"
          >
            {teamSubtitle}
          </Text>
        </HeaderWithMore>
        <div className="mobile-about__content">
          <Text
            type="body1"
            tag="div"
            color="gray_3"
            className="process-slider__content"
          >
            <MdContent content={aboutBody?.body} />
          </Text>
        </div>
        <div className="team-members">
          {teamMembers.map(
            ({
              name,
              role,
              slug,
              image: memberImage,
              githubPortfolio,
              linkedInPortfolio,
            }) => {
              const image = getImage(memberImage);
              return (
                <EmployeeCard
                  key={name}
                  name={name}
                  position={role}
                  slug={slug}
                  className="team-member"
                  imageUrl={image}
                  alt={`Image of ${name}`}
                  githubLink={githubPortfolio}
                  linkedInLink={linkedInPortfolio}
                />
              );
            },
          )}
        </div>
      </Content>

      <Content className="mobile-content-contact">
        <HeaderWithMore className="mobile-content-contact__title">
          <Text type="title3" tag="h3" color="gray_3">
            {contactName}
          </Text>
        </HeaderWithMore>
        <div className="mobile-content-contact__container">
          <div className="contact side-by-side">
            <div className="contact__content">
              <Text
                type="body1"
                tag="div"
                color="gray_3"
                className="contact__body-before"
              >
                {contactBodyBefore}
              </Text>
              <ContactList disableSocial />
              <Text type="body1" tag="div" color="gray_3">
                {contactBodyAfter}
              </Text>
            </div>
            <StayInTouch id="contactPageContactForm2" />
          </div>
        </div>
      </Content>

      <CallToAction
        hookId={ctaHookId}
        icon={ctaIcon}
        title={ctaTitle}
        body={ctaBody.ctaBody}
        className="mobile-content-cta"
      />
      <Social className="mobile-content-social" />
    </>
  );
};

const HomePageDesktopTemplate = ({ content }: IHomePageTemplate) => {
  const [, dispatch] = useStateValue();
  const {
    services,
    bannerTitle,
    bannerBody,
    bannerCta,
    processes,
    projects,
    technologies,
    pages,
    teamTitle,
    teamSubtitle,
    teamMembers,
    articles,
    contactTitle,
    contactSubtitle,
    contactBodyBefore,
    contactBodyAfter,
  } = content;
  const { name: servicesName, body: servicesBody } = pages.find(
    ({ slug }) => slug === `services`,
  );
  const { name: processesName, body: processesBody } = pages.find(
    ({ slug }) => slug === `processes`,
  );
  const { name: projectsName, body: projectsBody } = pages.find(
    ({ slug }) => slug === `projects`,
  );
  const { name: technologiesName, body: technologiesBody } = pages.find(
    ({ slug }) => slug === `tech`,
  );
  const {
    name: aboutName,
    body: aboutBody,
    subtitle: aboutSubtitle,
  } = pages.find(({ slug }) => slug === `about`);
  const { name: blogName, body: blogBody } = pages.find(
    ({ slug }) => slug === `blog`,
  );

  const servicesRef = useRef();
  const processesRef = useRef();
  const projectsRef = useRef();
  const techRef = useRef();
  const aboutRef = useRef();
  const blogRef = useRef();
  const contactRef = useRef();

  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#services` },
      }),
    servicesRef,
  );
  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#processes` },
      }),
    processesRef,
  );
  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#projects` },
      }),
    projectsRef,
  );
  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#tech` },
      }),
    techRef,
  );
  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#about` },
      }),
    aboutRef,
  );
  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#blog` },
      }),
    blogRef,
  );
  useActiveOnIntersect(
    () =>
      dispatch({
        type: `changeLocation`,
        newLocationState: { current: `#contact` },
      }),
    contactRef,
  );

  return (
    <>
      <section className="section" id="services">
        <SectionHeader
          className="services-header"
          icon={faBellConcierge}
          title={servicesName}
          subtitle={servicesBody?.body}
          ref={servicesRef}
        />
        <main className="main">
          <ServicesSlider services={services} slider={false} />
        </main>
      </section>

      <Banner title={bannerTitle} content={bannerBody} cta={bannerCta} />

      <section className="section" id="processes">
        <SectionHeader
          icon={faChain}
          title={processesName}
          subtitle={processesBody?.body}
          ref={processesRef}
        />
        <main className="main">
          <ProcessesSlider processes={processes} />
        </main>
      </section>

      <section className="section" id="projects">
        <SectionHeader
          icon={faRocket}
          title={projectsName}
          subtitle={projectsBody?.body}
          ref={projectsRef}
          className="project-slider-header"
        />
        <main className="main">
          <div className="projects">
            <ProjectsTable data={projects} />
          </div>
        </main>
      </section>

      <section className="section" id="tech">
        <SectionHeader
          icon={faGearCode}
          title={technologiesName}
          subtitle={technologiesBody?.body}
          ref={techRef}
          className="tech-slider-header"
        />
        <main className="main">
          <div className="technologies">
            <TechTable data={technologies} />
          </div>
        </main>
      </section>

      <section className="section" id="about">
        <SectionHeader
          icon={faInfo}
          title={aboutName}
          subtitle={aboutSubtitle}
          ref={aboutRef}
        />
        <main className="main">
          <div className="about__sub-header">
            <Text type="title3" tag="h3" color="gray_3">
              {teamTitle}
            </Text>
            <Text
              type="subtitle2"
              tag="h5"
              color="white_1_45"
              className="team-subtitle"
            >
              {teamSubtitle}
            </Text>
          </div>
          <div className="about three-by-two-fifth">
            <div className="about__members">
              <div className="team-members">
                {teamMembers.map(
                  ({
                    role,
                    slug,
                    name,
                    image: memberImage,
                    githubPortfolio,
                    linkedInPortfolio,
                  }) => {
                    const image = getImage(memberImage);
                    return (
                      <EmployeeCard
                        key={name}
                        name={name}
                        slug={slug}
                        position={role}
                        className="team-member"
                        imageUrl={image}
                        alt={`Image of ${name}`}
                        githubLink={githubPortfolio}
                        linkedInLink={linkedInPortfolio}
                      />
                    );
                  },
                )}
              </div>
            </div>
            <div className="about__content">
              <Text
                type="body1"
                tag="div"
                color="gray_3"
                className="process-slider__content"
              >
                <MdContent content={aboutBody?.body} />
              </Text>
            </div>
          </div>
        </main>
      </section>

      <section className="section" id="blog">
        <SectionHeader
          icon={faBlog}
          title={blogName}
          subtitle={blogBody?.body}
          ref={blogRef}
          className="blog-slider-header"
        />
        <main className="main">
          <div className="blog-container">
            <ArticlesTable articles={articles} />
          </div>
          <div className="blog-button">
            <Link to="/blog/">
              <Button size="small" disabled={false} iconRight={faChevronRight}>
                Enter blog
              </Button>
            </Link>
          </div>
        </main>
      </section>

      <section className="section" id="contact">
        <SectionHeader
          icon={faEnvelope}
          title={contactTitle}
          subtitle={contactSubtitle}
          ref={contactRef}
        />
        <main className="main">
          <div className="contact side-by-side">
            <div className="contact__content">
              <Text
                type="body1"
                tag="div"
                color="gray_3"
                className="contact__body-before"
              >
                {contactBodyBefore}
              </Text>
              <ContactList disableSocial />
              <Text type="body1" tag="div" color="gray_3">
                {contactBodyAfter}
              </Text>
            </div>

            <StayInTouch id="contactPageContactForm2" />
          </div>
        </main>
      </section>
    </>
  );
};

const HomePage = ({ data, location }: IHomePage) => {
  const homePage = get(data, `contentfulIndex`);
  const {
    title,
    titleSlides,
    slug,
    metaDescription,
    metaTitle,
    templateKey,
    heroImageAnimated,
    heroImage,
    svgTopPath,
    svgBottomPath,
    welcomeNote: { welcomeNote },
    servicesTitle,
    servicesSubtitle,
    services,
    bannerTitle,
    bannerBody,
    bannerCta,
    processes,
    projectsTitle,
    projects,
    technologiesTitle,
    technologies,
    pages,
    articlesTitle,
    articles,
    teamTitle,
    teamSubtitle,
    teamMembers,
    contactTitle,
    contactSubtitle,
    contactBodyBefore,
    contactBodyAfter,
    ctaContactTitle,
    ctaTitle,
    ctaHookId,
    ctaIcon: pageCTAIcon,
    ctaBody,
  } = homePage;
  const hero = getImage(heroImage);
  const ctaIcon = getImage(pageCTAIcon);

  return (
    <StyledHome>
      <Layout
        location={location}
        coverImage={hero}
        coverAnimation={heroImageAnimated.svg.originalContent}
        topSvg={svgTopPath.svg.content}
        bottomSvg={svgBottomPath.svg.content}
        desktopHeading={
          <Content restrictWidth>
            <Text
              noRhythem
              type="title1"
              tag="h2"
              color="white_1"
              className="main-title"
            >
              {title}
            </Text>
            <SlidingText list={titleSlides} />
            <Text type="body1" tag="div" color="gray_3">
              <MdContent content={welcomeNote} />
            </Text>

            <Link
              to="/presentation"
              state={{
                modal: true,
              }}
            >
              <Button size="large" disabled={false} iconLeft={faVideoCamera}>
                Play intro
              </Button>
            </Link>
            <AnchorLink to="/#services">
              <Button
                size="large"
                disabled={false}
                iconLeft={faArrowRightLong}
                noBg
                contrast
              >
                Get started
              </Button>
            </AnchorLink>
          </Content>
        }
        desktopBody={
          <HomePageDesktopTemplate
            content={{
              servicesTitle,
              servicesSubtitle,
              services,
              bannerTitle,
              bannerBody,
              bannerCta,
              processes,
              projectsTitle,
              projects,
              technologies,
              pages,
              articlesTitle,
              articles,
              teamTitle,
              teamSubtitle,
              teamMembers,
              contactTitle,
              contactSubtitle,
              contactBodyBefore,
              contactBodyAfter,
            }}
          />
        }
        desktopCta={
          <Content>
            <CallToAction
              hookId={ctaHookId}
              icon={ctaIcon}
              title={ctaTitle}
              body={ctaBody.ctaBody}
              maxWidth="100%"
              className="cta"
            />
            <Social />
          </Content>
        }
      >
        <Seo
          title={title}
          metaTitle={metaTitle}
          metaDesc={metaDescription}
          slug={slug}
          templateKey={templateKey}
        />
        <Main background="transparent" disableVertPadding={false}>
          <HomePageTemplate
            content={{
              title,
              titleSlides,
              welcomeNote,
              servicesTitle,
              servicesSubtitle,
              services,
              bannerTitle,
              bannerBody,
              bannerCta,
              projectsTitle,
              projects,
              technologiesTitle,
              technologies,
              articlesTitle,
              articles,
              teamTitle,
              teamSubtitle,
              teamMembers,
              ctaContactTitle,
              ctaTitle,
              ctaHookId,
              ctaIcon,
              ctaBody,
              pages,
              processes,
              contactTitle,
              contactSubtitle,
              contactBodyBefore,
              contactBodyAfter,
            }}
          />
        </Main>
      </Layout>
    </StyledHome>
  );
};

export default HomePage;

export const HomePageQuery = graphql`
  query HomePage($slug: String!) {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    contentfulIndex(slug: { eq: $slug }) {
      templateKey
      title
      titleSlides
      slug
      heroImageAnimated {
        svg {
          originalContent
        }
      }
      heroImage {
        gatsbyImageData(
          width: 560
          height: 900
          quality: 100
          placeholder: BLURRED
        )
      }
      pages {
        slug
        title
        subtitle
        name
        body {
          body
        }
        ctaBody {
          ctaBody
        }
      }
      svgTopPath {
        svg {
          content
        }
      }
      svgBottomPath {
        svg {
          content
        }
      }
      metaTitle
      metaDescription
      welcomeNote {
        welcomeNote
      }
      servicesTitle
      servicesSubtitle
      services {
        name
        slug
        excerpt {
          excerpt
        }
        icon {
          description
          gatsbyImageData(quality: 100, placeholder: BLURRED)
        }
        animatedStart {
          svg {
            originalContent
          }
        }
        animated {
          svg {
            originalContent
          }
        }
      }
      bannerTitle
      bannerBody
      bannerCta
      projectsTitle
      projects {
        name
        slug
        thumbnailTitle
        description {
          description
        }
        thumbnailImage {
          description
          gatsbyImageData(
            width: 225
            height: 120
            quality: 100
            placeholder: BLURRED
          )
        }
      }
      technologiesTitle
      technologies {
        title
        id
        url
        description {
          description
        }
        category
        image {
          title
          gatsbyImageData(
            width: 60
            height: 60
            quality: 100
            placeholder: BLURRED
          )
        }
      }
      processes {
        body {
          body
        }
        title
        subtitle
        order
        name
        displayId
        animationDesktop {
          svg {
            originalContent
          }
        }
        animationMobile {
          svg {
            originalContent
          }
        }
        image {
          gatsbyImageData(
            width: 372
            height: 279
            quality: 100
            placeholder: BLURRED
          )
        }
      }
      articlesTitle
      articles {
        title
        slug
        id
        timeToRead
        tags
        createDate(formatString: "MMMM Do, YYYY")
        thumbImage {
          description
          gatsbyImageData(width: 344, height: 180, quality: 100)
        }
        excerpt
        author {
          name
          slug
          image {
            gatsbyImageData(
              width: 40
              height: 40
              quality: 100
              placeholder: BLURRED
            )
          }
        }
      }
      teamTitle
      teamSubtitle
      teamMembers {
        image {
          gatsbyImageData(
            width: 72
            height: 72
            quality: 100
            placeholder: BLURRED
          )
        }
        name
        role
        slug
        githubPortfolio
        linkedInPortfolio
      }
      contactTitle
      contactSubtitle
      contactBodyBefore
      contactBodyAfter
      ctaContactTitle
      ctaTitle
      ctaHookId
      ctaIcon {
        gatsbyImageData(width: 48, quality: 100, placeholder: BLURRED)
      }
      ctaBody {
        ctaBody
      }
    }
  }
`;
